import onetime from "@-/com.npmjs.onetime";
import delay from "@-/com.npmjs.delay";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { Platform } from "@-/com.npmjs.react-native";
import { nativeProcessStats } from "@-/dev.improve.haniverse.modules.process-stats";
import {
  getDiagnosticsRoute,
  isDiagnosticsRecording,
  recordDiagnosticsEvent,
} from "#diagnostics-store";
import { readJsHeapStats } from "#js-runtime-stats";
import { readUiRuntimeMemory, uiRuntimeQueueBarrier } from "#ui-runtime-memory";
import type { UiRuntimeMemoryDiagnostic } from "#ui-runtime-memory-types";

/**
 * One read of the UI runtime's memory (optionally collecting first) and one hop through the UI
 * queue: native calls that answer within a frame or two, or a GC pause. A UI runtime that never
 * answers must end the diagnostic with an error instead of leaving its action spinning.
 */
const UI_RUNTIME_CALL_DEADLINE_MS = 10_000;

export function canCollectUiRuntimeGarbage(): boolean {
  return Platform.OS === "ios" && typeof nativeProcessStats?.collectGarbage === "function";
}

function requireRecording() {
  if (!isDiagnosticsRecording()) {
    throw new Error("Diagnostic recording stopped or is off; no further collection will run.");
  }
}

function recordPhase(phase: string, data: Record<string, unknown> = {}) {
  recordDiagnosticsEvent({
    kind: "marker",
    label: `Explicit UI-then-main GC ${phase}: ${JSON.stringify({
      ...data,
      jsHeap: readJsHeapStats(),
      route: getDiagnosticsRoute(),
    })}`,
    timestamp: Date.now(),
  });
}

/** Explicit diagnostic operation with no screen lifetime or automatic sampling ownership. */
export function createUiRuntimeGarbageCollector({
  collectMain,
  readUiMemory,
  uiBarrier,
}: {
  collectMain: (() => void) | undefined;
  readUiMemory: (collect: boolean) => Promise<UiRuntimeMemoryDiagnostic>;
  uiBarrier: () => Promise<void>;
}) {
  let pending: Promise<void> | undefined;
  return function collect(): Promise<void> {
    if (pending !== undefined) {
      return pending;
    }
    pending = (async () => {
      if (collectMain === undefined) {
        throw new Error("Garbage collection diagnostics are unavailable in this build.");
      }
      requireRecording();
      try {
        // Read-only preflight rejects older binaries before starting the 30-second wait.
        const ui = await withDeadline(
          { deadline: UI_RUNTIME_CALL_DEADLINE_MS, operation: "read UI runtime memory" },
          () => readUiMemory(false),
        );
        requireRecording();
        recordPhase("scheduled", { delayMs: 30_000, ui });
        await delay(30_000);
        requireRecording();
        recordPhase("UI started");
        const collected = await withDeadline(
          { deadline: UI_RUNTIME_CALL_DEADLINE_MS, operation: "collect UI runtime garbage" },
          () => readUiMemory(true),
        );
        recordPhase("UI completed", { ui: collected });
        // The return Promise may resolve before its UI callback's JSI scope unwinds.
        // A subsequent UI job, then a later RN task, crosses both queue boundaries.
        // This is not a global flush of future callbacks, timers or other runtimes.
        await withDeadline(
          { deadline: UI_RUNTIME_CALL_DEADLINE_MS, operation: "UI runtime queue barrier" },
          () => uiBarrier(),
        );
        await delay(0);
        requireRecording();
        recordPhase("main started", { queueBarrier: "UI job then RN task" });
        const started = performance.now();
        collectMain();
        recordPhase("main completed", { durationMs: performance.now() - started });
      } catch (error) {
        recordPhase("failed", { message: error instanceof Error ? error.message : String(error) });
        throw error;
      }
    })().finally(() => {
      pending = undefined;
    });
    return pending;
  };
}

const getCollector = onetime(() =>
  createUiRuntimeGarbageCollector({
    collectMain: nativeProcessStats?.collectGarbage?.bind(nativeProcessStats),
    readUiMemory: readUiRuntimeMemory,
    uiBarrier: uiRuntimeQueueBarrier,
  }),
);

/** Only called by the explicit Diagnostics action. Never a production memory policy. */
export function collectUiRuntimeGarbage(): Promise<void> {
  return getCollector()();
}
